import type * as api from "./api";
import { memberName } from "./memberNames";

/** Rule Q-36: the text of the salespeople of an area. */
export function salespeopleText(people: api.Person[]): string {
  return people.length ? people.map((person) => memberName(person.email)).join(", ") : "None — new leads go to the inbox";
}

const SWITCH_CHOICES: { key: keyof api.SwitchPreview["choices"]; label: string }[] = [
  { key: "tomorrow", label: "Tomorrow" },
  { key: "rotation_week", label: "Next rotation week" },
  { key: "cycle", label: "Next cycle" },
];

/** The switch date choices in date order. */
export const switchChoices = (choices: api.SwitchPreview["choices"]) =>
  SWITCH_CHOICES.map((choice) => ({ ...choice, day: choices[choice.key] }))
    .filter((choice) => choice.day)
    .sort((a, b) => a.day.localeCompare(b.day));

type Switchable = Pick<api.SalesArea, "pickup_source" | "app_from" | "switch_state">;

/** Design 26.11 F4: sheet, scheduled or app. An older API sends no state; then the source is the state. */
export const switchState = (area: Switchable): api.DataSourceState => area.switch_state ?? (area.pickup_source === "app" ? "app" : "sheet");

/** Design 26.5 T2: where the counted pickups of the area come from. */
export function sourceText(area: Switchable): string {
  const state = switchState(area);
  if (state === "scheduled") return `Sheet → app on ${area.app_from ?? "a later date"}`;
  return state === "app" ? `App from ${area.app_from ?? "today"}` : "Sheet";
}

export const polygonText = (set: boolean) => (set ? "Set" : "None: no lead goes here");

/** The chosen ids with one id added or removed, in the order of the choice. */
export function toggled(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((found) => found !== id) : [...ids, id];
}

/** The saved salespeople who can still own a lead: the start of a change. */
export function startIds(owners: api.Person[], chosen: api.Person[]): string[] {
  return chosen.filter((person) => owners.some((owner) => owner.id === person.id)).map((person) => person.id);
}

export function sameIds(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/** The status line after a switch of the pickup source. */
export function savedSourceText(code: string, area: Switchable): string {
  const state = switchState(area);
  if (state === "scheduled") return `Saved: area ${code} goes to the app on ${area.app_from}. You can cancel it until that day.`;
  if (state === "app") return `Saved: area ${code} uses the app pickups${area.app_from ? ` from ${area.app_from}` : ""}.`;
  return `Saved: area ${code} uses the sheet pickups.`;
}

export type SwitchMode = "schedule" | "cancel" | "back";

/** The actions of the Pickups cell for each state of the area. */
export function switchModes(area: Switchable): SwitchMode[] {
  const state = switchState(area);
  if (state === "scheduled") return ["schedule", "cancel"];
  return state === "app" ? ["back"] : ["schedule"];
}

export function switchButtonText(mode: SwitchMode, state: api.DataSourceState): string {
  if (mode === "cancel") return "Cancel the switch…";
  if (mode === "back") return "Use the sheet…";
  return state === "scheduled" ? "Change the date…" : "Use the app…";
}

/** The sheet columns of the issues that a switch back writes again (rule T-9). */
export const WRITE_COLUMNS: Record<string, string> = {
  "#": "Gone from the sheet: set to OFF",
  customer: "Name",
  address: "Address",
  status: "Status",
  commercial: "Contract status",
  area: "Area",
  wkgroup: "Week group",
  start: "Window",
  duration: "Service time",
  "gal/wk": "Gallons a week",
  "tank gal": "Container size",
  latitude: "Container pin",
};

/** A count with its noun: "1 account", "3 accounts". */
export const counted = (n: number, noun: string) => `${n} ${noun}${n === 1 ? "" : "s"}`;

/** The confirm text of the panel. */
export function confirmText(mode: SwitchMode, state: api.DataSourceState, day: string): string {
  if (mode === "cancel") return "Cancel the switch";
  if (mode === "back") return "Switch back to the sheet";
  return state === "scheduled" ? `Move the switch to ${day}` : `Switch on ${day}`;
}

/** Design 26.11 F8, rule V-9: an area code such as S3.04. The letter is the region, the digit after it the rotation week. */
export const AREA_CODE = /^[NSEW][1-4]\.\d\d$/;

export const AREA_CODE_TEXT = "An area code is like S3.04: the region N, S, E or W, the rotation week 1 to 4, a point and two digits.";

export const areaCode = (text: string): string | null => {
  const code = text.trim().toUpperCase();
  return AREA_CODE.test(code) ? code : null;
};
